import {AppApi} from 'store/customer/api';
import {getValidStructure} from 'hooks/QFP/Shared';
import {mapBooleans} from 'components/customer/QFPRedux/helpers/mapBooleans';
import {mapHiddenMaterials} from 'components/customer/Product/helpers/mapHiddenMaterials';
import {FingerPullStyle} from 'components/customer/Product/entity/FingerPullStyle';
import {
    Appliance,
    ApplianceDB,
} from 'components/customer/Product/entity/Appliance';
import {ProductApi as ProductApiInterface} from 'components/customer/Product/entity/ProductApi';
import {productsLoadedSet} from 'components/customer/Product/store/roomProductsSlice';
import {Product} from 'components/customer/Product/entity/Product';
import {ProductForm} from 'components/customer/Product/entity/ProductForm';
import {HardwareOption} from 'shared/types/HardwareOption';
import {HingeStyle} from 'shared/types/HingeStyle';
import {ProductDefault} from 'components/customer/Product/entity/ProductDefault';
import {ValidStructureInterface} from 'components/customer/Product/entity/ValidStructureInterface';
import IDBProduct from 'IndexedDB/models/Product';
import {FetchArgs} from '@reduxjs/toolkit/query';
import {
    RoomLegacy,
    RoomResponse,
} from 'components/customer/Product/entity/Room';
import {mapRoom} from 'components/customer/Product/helpers/mapRoom';
import {normalizeProductWithDrawers} from 'hooks/Product/helpers/normalizeDrawerValues';
import {Cabinet} from 'components/customer/Product/entity/Cabinet';
import {mapJobCabinetsToProducts} from 'components/customer/Product/helpers/mapJobCabinetsToProducts';
import {
    mapAppliance,
    mapJobCabinetAppliances,
} from 'components/customer/Product/Appliance/helpers/mapAppliance';
import ProductStyle from 'IndexedDB/models/ProductStyle';
import {Template3D} from 'components/customer/RoomPlanner/types';
import {mapPreviewTemplate} from 'components/customer/RoomPlanner/helpers/mapPreviewTemplate';

export const RANGEHOOD = 1;

const getApplianceType = (type?: number) => {
    const APPLIANCES = ['Oven', 'Rangehood', 'Microwave'];

    return APPLIANCES[Number(type)];
};

export interface QuickAddParams {
    toeKickHeight?: number;
    baseHeight?: number;
    baseDepth?: number;
    upperHeight?: number;
    upperDepth?: number;
    tallHeight?: number;
    tallDepth?: number;
}

export const ProductApi = AppApi.injectEndpoints({
    endpoints: (builder) => ({
        getProductData: builder.query<
            Product,
            {
                cabinetType?: number;
                roomId?: number;
                cabinetId?: number;
                materialId?: number;
                edgeId?: number;
                doorId?: number;
                doorFilter?: string;
            }
        >({
            query: ({
                cabinetType,
                roomId,
                cabinetId,
                materialId,
                edgeId,
                doorId,
                doorFilter,
            }) => {
                if (cabinetId) {
                    return `cabinets/jobcabinet/id/${cabinetId}`;
                }

                const searchParams: {
                    material_id?: string;
                    edge_id?: string;
                    door_id?: string;
                    door_filter?: string;
                } = {};

                if (typeof materialId !== 'undefined') {
                    searchParams.material_id = String(materialId);
                }

                if (typeof edgeId !== 'undefined') {
                    searchParams.edge_id = String(edgeId);
                }

                if (typeof doorId !== 'undefined') {
                    searchParams.door_id = String(doorId);
                }

                if (typeof doorFilter !== 'undefined') {
                    searchParams.door_filter = doorFilter;
                }

                const search =
                    Object.keys(searchParams).length > 0
                        ? '?' + new URLSearchParams(searchParams).toString()
                        : '';

                return `cabinets/jobcabinet/cabinetType/${cabinetType}/roomId/${roomId}${search}`;
            },
            transformResponse: (response: {job_cabinet: Product}) => {
                const data = mapHiddenMaterials(
                    mapJobCabinetAppliances(response.job_cabinet)
                );
                const normalized = normalizeProductWithDrawers(data);

                return {
                    ...normalized,
                };
            },
            providesTags: (result, error, props) => [
                {type: 'ProductDefaultRoom', ...props},
            ],
        }),
        getProductDefault: builder.query<ProductDefault, {cabinetType: number}>(
            {
                query: ({cabinetType}) => `cabinets/formfields/${cabinetType}`,
                transformErrorResponse: (response) => response,
                providesTags: (result, error, {cabinetType}) => [
                    {type: 'ProductDefault', cabinetType},
                ],
            }
        ),
        getProductStructure: builder.query<ProductForm, {cabinetType: number}>({
            query: ({cabinetType}) => `cabinets/data/fields/${cabinetType}`,
            transformResponse: (response: ProductForm) => response,
            providesTags: (result, error, {cabinetType}) => [
                {type: 'ProductStructure', cabinetType},
            ],
        }),
        getQFPProductStructure: builder.query<
            ValidStructureInterface,
            {cabinetType: number; qfp?: boolean}
        >({
            query: ({cabinetType}) => {
                if (typeof cabinetType === 'undefined') {
                    throw new Error('Please provide cabinet type');
                }

                return `cabinets/data/fields/${cabinetType}`;
            },
            transformResponse: (response: ProductForm, _, {qfp = true}) => {
                const validStructure = getValidStructure(
                    response.structure
                ) as ValidStructureInterface;
                validStructure.validation = response.validation;

                if (qfp) {
                    validStructure.validation.push({
                        fields: ['custom_colour'],
                        message: 'Please enter custom color value',
                        rule: 'LEN(custom_colour) > 0',
                        enabled:
                            'AND(cabinet_ext_colour.customColour, hasCustomColour)',
                        type: 'single',
                    });
                }

                validStructure.originalStructure = response.structure;
                validStructure.majorDimensions = response.majorDimensions;

                return mapBooleans(validStructure);
            },
            providesTags: (result, error, {cabinetType}) => [
                {type: 'QFPProductStructure', cabinetType},
            ],
        }),
        getHardwareOptions: builder.query<HardwareOption[], void>({
            query: () => 'customer/manufacturerHardwareOptions',
            transformResponse: (response: {
                hardware_options: HardwareOption[];
            }) => response.hardware_options,
            providesTags: [{type: 'HardwareOptions'}],
        }),
        getHingeStyles: builder.query<HingeStyle[], {cabinetType: number}>({
            query: ({cabinetType}) => `hingeStyle/cabinet/${cabinetType}`,
            transformResponse: (response: {hingeStyles: HingeStyle[]}) =>
                response.hingeStyles,
            providesTags: (result, error, {cabinetType}) => [
                {
                    type: 'HingeStyles',
                    cabinetType,
                },
            ],
        }),
        getCabinets: builder.query<Product[], {roomId: number}>({
            query: ({roomId}) => `jobcabinets/room/${roomId}/qfp`,
            transformResponse: (response: {
                existing_quick_flat_products: Product[];
            }) => {
                return response.existing_quick_flat_products.sort(
                    (a, b) => a.room_cab_number - b.room_cab_number
                );
            },
            providesTags: (response, error, {roomId}) => [
                {type: 'Cabinets', roomId},
            ],
        }),
        deleteCabinet: builder.mutation<void, {cabinetId: number}>({
            query: ({cabinetId}) => ({
                url: `cabinets/jobcabinet/${cabinetId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['JobCabinetVariationDetails'],
            // NOTE: Not invalidating these here as qfp will save multiple
            // products and will probably will make multiple network request
            // invalidatesTags: ['Cabinets', 'JobCost', 'ProductRoom'],
        }),
        saveProduct: builder.mutation<
            {id: number; roomCabNumber: number},
            {product: Product}
        >({
            query: ({product}) => {
                const {product: body, formData} = mapAppliance(product);

                const headers =
                    formData === null
                        ? {
                              'Content-Type': 'application/json',
                              'X-Requested-With': 'XMLHttpRequest',
                          }
                        : undefined;

                return {
                    url: 'cabinets/jobcabinet/save',
                    method: 'POST',
                    headers,
                    body: formData === null ? JSON.stringify(body) : formData,
                };
            },
            transformResponse: (response: {
                id: number;
                roomCabNumber: number;
            }) => ({id: response.id, roomCabNumber: response.roomCabNumber}),
            invalidatesTags: [
                'Variations',
                'JobCabinetVariationDetails',
                'Job',
                'ProductDefaultRoom',
            ],
            // NOTE: Not invalidating these here as qfp will save multiple
            // products and will probably will make multiple network request
            // invalidatesTags: ['JobCost', 'ProductRoom'],
        }),
        getRoomProducts: builder.query<
            {
                products: Product[];
                roomDetails: RoomLegacy;
            },
            {roomId: number}
        >({
            query: ({roomId}) => `room/${roomId}`,
            onQueryStarted: (arg, {dispatch, queryFulfilled}) => {
                dispatch(productsLoadedSet(false));
                queryFulfilled
                    .then(() => {
                        dispatch(productsLoadedSet(true));
                    })
                    .catch(() => {
                        dispatch(productsLoadedSet(true));
                    });
            },
            transformResponse: (response: {
                job_cabinets: ProductApiInterface[];
                room: RoomResponse;
            }) => {
                if (response) {
                    return {
                        roomDetails: response?.room
                            ? mapRoom(response.room)
                            : ({} as RoomLegacy),
                        products: mapJobCabinetsToProducts(
                            response?.job_cabinets
                        ),
                    };
                }
                return {
                    products: [],
                    roomDetails: undefined,
                };
            },
            providesTags: (response, error, {roomId}) => [
                {type: 'ProductRoom', id: roomId},
            ],
        }),
        updateProductQuantity: builder.mutation<
            void,
            {cabinetId: number; quantity: number; index?: number}
        >({
            query: ({cabinetId, quantity, index}) => {
                const query: FetchArgs = {
                    url: `jobcabinet/${cabinetId}/quantity/${quantity}`,
                    method: 'PUT',
                };

                if (index != undefined) {
                    query.body = {
                        layout_room_product_index: index,
                    };
                }

                return query;
            },
            invalidatesTags: ['JobCost', 'ProductRoom', 'TotalProductCount'],
        }),
        updateOrder: builder.mutation<
            void,
            {jobId: number; roomId: number; order: {[key: number]: number}}
        >({
            query: ({jobId, roomId, order}) => ({
                url: `jobs/${jobId}/room/${roomId}/reorder`,
                method: 'POST',
                body: order,
            }),
            invalidatesTags: ['ProductRoom', 'JobCost'],
        }),
        resetKeys: builder.mutation<void, {jobId: number}>({
            query: ({jobId}) => ({
                url: `jobs/${jobId}/reset_keys`,
                method: 'POST',
            }),
        }),
        getFingerPullStyles: builder.query<
            FingerPullStyle[],
            {drawerGap?: number; topMargin?: number}
        >({
            query: () => 'fingerpull',
            transformResponse: (
                response: {items: FingerPullStyle[]},
                _,
                {drawerGap = 0, topMargin = 0}
            ) => {
                const result = response?.items || [];

                result.unshift({id: -1, name: 'None', drawerGap, topMargin});

                return result;
            },
            providesTags: ['FingerPullStyles'],
        }),
        getAppliances: builder.query<Appliance[], void>({
            query: () => 'customer/kitchen-appliances',
            transformResponse: (response: {data: ApplianceDB[]}) => {
                return response.data.map((appliance) => {
                    const applianceType = getApplianceType(appliance.type);
                    let applianceName = `${appliance.make}: ${appliance.model}; H:${appliance.height} x W:${appliance.width} x D:${appliance.depth}`;

                    if (applianceType == 'Rangehood') {
                        applianceName = `${appliance.make}: ${appliance.model}`;
                    }

                    return {
                        id: appliance.id,
                        name: applianceName,
                        image: appliance.imageName
                            ? `uploads/images/${applianceType}/${
                                  appliance.imageName
                              }`
                            : undefined,
                        type: appliance.type,
                        height: appliance.height,
                        width: appliance.width,
                        depth: appliance.depth,
                    };
                });
            },
            providesTags: ['Appliances'],
        }),
        getQuickProducts: builder.query<
            IDBProduct[],
            QuickAddParams | undefined
        >({
            query: (params) => {
                if (params) {
                    const {
                        toeKickHeight,
                        baseDepth,
                        baseHeight,
                        upperDepth,
                        upperHeight,
                        tallDepth,
                        tallHeight,
                    } = params;
                    const urlParams = new URLSearchParams();
                    if (!isNaN(Number(toeKickHeight))) {
                        urlParams.append(
                            'toe_kick.height',
                            String(toeKickHeight)
                        );
                    }
                    if (baseDepth) {
                        urlParams.append('base.depth', String(baseDepth));
                    }
                    if (baseHeight) {
                        urlParams.append('base.height', String(baseHeight));
                    }
                    if (upperDepth) {
                        urlParams.append('upper.depth', String(upperDepth));
                    }
                    if (upperHeight) {
                        urlParams.append('upper.height', String(upperHeight));
                    }
                    if (tallDepth) {
                        urlParams.append('tall.depth', String(tallDepth));
                    }
                    if (tallHeight) {
                        urlParams.append('tall.height', String(tallHeight));
                    }

                    return {
                        url: 'cabinets/quickproducts',
                        params: urlParams,
                    };
                }

                return 'cabinets/quickproducts';
            },
            transformResponse: (response: {products: IDBProduct[]}) => {
                return response.products.map((product) => ({
                    quickProducts: 1,
                    ...product,
                }));
            },
            providesTags: (result, error, params) => {
                if (params && Object.keys(params).length > 0) {
                    return [
                        {type: 'QuickProducts', id: JSON.stringify(params)},
                    ];
                }

                return ['QuickProducts'];
            },
        }),
        getPreviewTemplate: builder.query<
            Template3D | null,
            {productId: string}
        >({
            query: ({productId}) => ({
                url: `cabinet/${productId}/preview-template`,
                headers: {Accept: 'application/xml, text/xml'},
                responseHandler: 'text',
            }),
            transformResponse: (xml: string, meta, {productId}) =>
                mapPreviewTemplate(xml, productId),
            forceRefetch: () => true,
        }),
        addAppliance: builder.mutation<number, ApplianceDB>({
            query: (body) => ({
                url: 'customer/kitchen-appliances/save',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Appliances'],
            transformResponse: (result: {data: number}) => {
                return result.data;
            },
        }),
        quickAddProduct: builder.mutation<
            number,
            {
                cabinetType: number;
                room: number;
                quantity: number;
                width: number;
            }
        >({
            query: ({cabinetType, room, quantity, width}) => ({
                url: `cabinets/quickjobcabinet/cabinetType/${cabinetType}/roomId/${room}/quantity/${quantity}/width/${width}`,
                method: 'GET',
            }),
            transformResponse: (response: {cabinet: {id: number}}) => {
                return response.cabinet.id;
            },
            transformErrorResponse: (response, meta) => {
                if (response.status === 503) {
                    if (meta?.response?.headers?.has('retry-after')) {
                        const retryAfter =
                            meta.response.headers.get('retry-after');
                        let error =
                            'Could not save the product, please try again later.';

                        if (
                            typeof response === 'object' &&
                            'data' in response
                        ) {
                            const {data} = response as {
                                data: {error: string};
                            };

                            if (data) {
                                if ('error' in data) {
                                    error = data.error;
                                }
                            }
                        }

                        return {
                            data: {
                                error,
                                retryAfter: parseInt(retryAfter || '0'),
                            },
                        };
                    }
                }

                return response;
            },
            invalidatesTags: [
                'Variations',
                'JobCabinetVariationDetails',
                'Job',
            ],
        }),
        updateFields: builder.mutation<void, Partial<Product>>({
            query: ({job_cabinet_id: jobCabinetId, ...body}) => ({
                url: `jobcabinet/${jobCabinetId}/field`,
                method: 'PUT',
                body,
            }),
            async onQueryStarted({}, {dispatch, queryFulfilled}) {
                const response = await queryFulfilled;

                if (!('error' in response)) {
                    dispatch(ProductApi.util.invalidateTags(['ProductRoom']));
                }
            },
            invalidatesTags: ['ProductDefaultRoom'],
        }),
        getCabinetDetails: builder.query<Cabinet, {cabinetId: number}>({
            query: ({cabinetId}) => `cabinets/cabinet/${cabinetId}`,
            transformResponse: (response: {cabinet: Cabinet}) => {
                return response.cabinet;
            },
            providesTags: (result, error, {cabinetId}) => [
                {type: 'Cabinet', id: cabinetId},
            ],
        }),
        getProductStyles: builder.query<ProductStyle[], void>({
            query: () => 'cabinets/productStyles',
            transformResponse: (response: {product_styles: ProductStyle[]}) =>
                response.product_styles,
            providesTags: ['ProductStyles'],
        }),
    }),
});

export const invalidateQuickProducts = () =>
    ProductApi.util.invalidateTags(['QuickProducts']);
export const invalidateJobAndCabinet = () =>
    ProductApi.util.invalidateTags(['Job', 'Cabinets']);
export const invalidateSidebarAndCost = () =>
    ProductApi.util.invalidateTags(['JobCost', 'ProductRoom']);
export const invalidateParticularRoomAndCost = (roomId: number) =>
    ProductApi.util.invalidateTags([
        'JobCost',
        {type: 'ProductRoom', id: roomId},
    ]);
export const invalidateProductAndCost = () =>
    ProductApi.util.invalidateTags([
        'Job',
        'JobCost',
        'ProductRoom',
        'Cabinets',
    ]);
export const invalidateProductDefaultByRoom = () =>
    ProductApi.util.invalidateTags(['ProductDefaultRoom']);
export const invalidateProductDefault = () =>
    ProductApi.util.invalidateTags(['ProductDefault']);
export const invalidateJob = () => ProductApi.util.invalidateTags(['Job']);
export const {
    useGetProductDataQuery,
    useLazyGetProductDataQuery,
    useLazyGetProductStructureQuery,
    useGetHardwareOptionsQuery,
    useGetHingeStylesQuery,
    useLazyGetProductDefaultQuery,
    useGetProductDefaultQuery,
    useLazyGetQFPProductStructureQuery,
    useGetQFPProductStructureQuery,
    useLazyGetCabinetsQuery,
    useDeleteCabinetMutation,
    useSaveProductMutation,
    useGetRoomProductsQuery,
    useLazyGetRoomProductsQuery,
    useUpdateProductQuantityMutation,
    useUpdateOrderMutation,
    useResetKeysMutation,
    useGetProductStructureQuery,
    useLazyGetFingerPullStylesQuery,
    useGetAppliancesQuery,
    useAddApplianceMutation,
    useUpdateFieldsMutation,
    useQuickAddProductMutation,
    useGetQuickProductsQuery,
    useLazyGetQuickProductsQuery,
    useLazyGetCabinetDetailsQuery,
    useGetProductStylesQuery,
} = ProductApi;
